// src/components/EditAlumni.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaEdit } from 'react-icons/fa';
import { parseEducation, formatEducation } from '../utils/education';
import { supabase } from '../lib/supabase';

const EditAlumni = () => {
  const { id } = useParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [s1Kampus, setS1Kampus] = useState('');
  const [s1Jurusan, setS1Jurusan] = useState('');
  const [s2Kampus, setS2Kampus] = useState('');
  const [s2Jurusan, setS2Jurusan] = useState('');
  const [s3Kampus, setS3Kampus] = useState('');
  const [s3Jurusan, setS3Jurusan] = useState('');
  const [job, setJob] = useState('');
  const [institution, setInstitution] = useState('');
  const [researchInterests, setResearchInterests] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [googleScholar, setGoogleScholar] = useState('');
  const [tahunPimnas, setTahunPimnas] = useState('');
  const [domisili, setDomisili] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    supabase.from('alumni').select('*').eq('id', parseInt(id, 10)).single()
      .then(({ data: a, error }) => {
        if (error || !a) { setErrorMessage('Gagal memuat data alumni.'); return; }
        setName(a.name || '');
        setEmail(a.email || '');
        const edu = parseEducation(a.education);
        setS1Kampus(edu.s1_kampus);
        setS1Jurusan(edu.s1_jurusan);
        setS2Kampus(edu.s2_kampus);
        setS2Jurusan(edu.s2_jurusan);
        setS3Kampus(edu.s3_kampus);
        setS3Jurusan(edu.s3_jurusan);
        setJob(a.job || '');
        setInstitution(a.institution || '');
        setResearchInterests(a.research_interests || '');
        setLinkedin(a.linkedin || '');
        setGoogleScholar(a.google_scholar || '');
        setTahunPimnas(a.tahun_pimnas != null ? String(a.tahun_pimnas) : '');
        setDomisili(a.domisili || '');
        setPhotoUrl(a.photo_url || '');
      })
      .finally(() => setFetching(false));
  }, [id]);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 1 * 1024 * 1024) {
      setErrorMessage('Ukuran foto maksimal 1 MB. Silakan kompres atau pilih foto lain.');
      e.target.value = '';
      return;
    }
    setErrorMessage('');
    setPhoto(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (!name.trim()) {
      setErrorMessage('Nama lengkap wajib diisi.');
      setLoading(false);
      return;
    }

    try {
      let newPhotoUrl = undefined;

      if (photo) {
        const fileExt = photo.name.split('.').pop();
        const fileName = `${(email || name).replace(/[^a-zA-Z0-9]/g, '')}-${Date.now()}.${fileExt}`;
        const filePath = `alumni-photos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('alumni-photos')
          .upload(filePath, photo, { contentType: photo.type, cacheControl: '3600', upsert: false });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('alumni-photos').getPublicUrl(filePath);
        newPhotoUrl = urlData.publicUrl;
      }

      const updatePayload = {
        name: name.trim(),
        email: email.trim() || null,
        education: formatEducation({ s1_kampus: s1Kampus, s1_jurusan: s1Jurusan, s2_kampus: s2Kampus, s2_jurusan: s2Jurusan, s3_kampus: s3Kampus, s3_jurusan: s3Jurusan }),
        job: job.trim() || null,
        institution: institution.trim() || null,
        research_interests: researchInterests.trim() || null,
        tahun_pimnas: tahunPimnas ? parseInt(tahunPimnas, 10) : null,
        domisili: domisili.trim() || null,
        linkedin: linkedin || null,
        google_scholar: googleScholar || null,
      };
      if (newPhotoUrl) updatePayload.photo_url = newPhotoUrl;

      const { error } = await supabase.from('alumni').update(updatePayload).eq('id', parseInt(id, 10));
      if (error) throw error;

      alert('Alumni berhasil diperbarui!');
      navigate('/alumni');
    } catch (error) {
      console.error('Error updating alumni:', error);
      setErrorMessage('Gagal memperbarui alumni: ' + (error.message || 'Silakan coba lagi.'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';
  const optLabel = <span className="text-gray-400 font-normal">(Opsional)</span>;

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/alumni" className="text-gray-300 hover:text-white text-sm inline-flex items-center gap-1 mb-4 transition-colors">
            <FaArrowLeft /> Kembali ke Direktori
          </Link>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FaEdit className="text-gold-400" /> Edit Data Alumni
          </h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-gold-500 to-primary-500"></div>

          <div className="p-6 md:p-8">
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Photo */}
              <div className="flex flex-col items-center mb-2">
                <div className="relative mb-3">
                  {(preview || photoUrl) ? (
                    <img src={preview || photoUrl} alt="Foto" className="w-24 h-24 rounded-full object-cover ring-4 ring-primary-100" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-3xl font-bold">
                      {name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <label className="cursor-pointer text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                  Ganti Foto {optLabel}
                  <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
                </label>
              </div>

              {/* Identitas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Nama Lengkap *</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} required className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Email {optLabel}</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
                </div>
              </div>

              {/* Tahun PIMNAS */}
              <div className="bg-gold-500/5 border border-gold-500/20 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  🏆 Partisipasi PIMNAS
                </h3>
                <div className="max-w-xs">
                  <label className={labelClass}>PIMNAS ke- {optLabel}</label>
                  <input type="number" min="1" max="50" value={tahunPimnas} onChange={e => setTahunPimnas(e.target.value)} placeholder="Contoh: 33" className={inputClass} />
                  <p className="text-xs text-gray-400 mt-1">Nomor edisi PIMNAS yang diikuti</p>
                </div>
              </div>

              {/* Pendidikan Section */}
              <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  🎓 Riwayat Pendidikan <span className="text-xs text-gray-400 font-normal">— Isi yang relevan saja</span>
                </h3>

                {/* S1 */}
                <div>
                  <p className="text-xs font-semibold text-primary-600 mb-2">S1 — Almamater PIMNAS</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Kampus {optLabel}</label>
                      <input type="text" value={s1Kampus} onChange={e => setS1Kampus(e.target.value)} placeholder="Universitas / Institut" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Jurusan / Prodi {optLabel}</label>
                      <input type="text" value={s1Jurusan} onChange={e => setS1Jurusan(e.target.value)} placeholder="Teknik Informatika, Biologi, dll" className={inputClass} />
                    </div>
                  </div>
                </div>

                {/* S2 */}
                <div>
                  <p className="text-xs font-semibold text-blue-600 mb-2">S2 — Magister</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Kampus {optLabel}</label>
                      <input type="text" value={s2Kampus} onChange={e => setS2Kampus(e.target.value)} placeholder="Universitas S2" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Jurusan / Prodi {optLabel}</label>
                      <input type="text" value={s2Jurusan} onChange={e => setS2Jurusan(e.target.value)} placeholder="Jurusan S2" className={inputClass} />
                    </div>
                  </div>
                </div>

                {/* S3 */}
                <div>
                  <p className="text-xs font-semibold text-purple-600 mb-2">S3 — Doktoral</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Kampus {optLabel}</label>
                      <input type="text" value={s3Kampus} onChange={e => setS3Kampus(e.target.value)} placeholder="Universitas S3" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Jurusan / Prodi {optLabel}</label>
                      <input type="text" value={s3Jurusan} onChange={e => setS3Jurusan(e.target.value)} placeholder="Jurusan S3" className={inputClass} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Karir */}
              <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  💼 Karir & Pekerjaan
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Pekerjaan / Jabatan {optLabel}</label>
                    <input type="text" value={job} onChange={e => setJob(e.target.value)} placeholder="Dosen, Peneliti, Software Engineer, dll" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Instansi / Tempat Kerja {optLabel}</label>
                    <input type="text" value={institution} onChange={e => setInstitution(e.target.value)} placeholder="Universitas, Perusahaan, Lembaga, dll" className={inputClass} />
                  </div>
                </div>
              </div>

              {/* Minat Riset */}
              <div>
                <label className={labelClass}>Minat Riset / Bidang Keahlian {optLabel}</label>
                <input type="text" value={researchInterests} onChange={e => setResearchInterests(e.target.value)} placeholder="Contoh: Affective Computing, NLP, IoT, Bioteknologi" className={inputClass} />
                <p className="text-xs text-gray-400 mt-1">Pisahkan dengan koma jika lebih dari satu bidang</p>
              </div>

              {/* Domisili */}
              <div>
                <label className={labelClass}>Domisili Saat Ini {optLabel}</label>
                <input type="text" value={domisili} onChange={e => setDomisili(e.target.value)} placeholder="Contoh: Jakarta, Bandung, Surabaya" className={inputClass} />
                <p className="text-xs text-gray-400 mt-1">Kota tempat tinggal saat ini — untuk peta persebaran alumni</p>
              </div>

              {/* Profil Online */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>LinkedIn {optLabel}</label>
                  <input type="url" value={linkedin} onChange={e => setLinkedin(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Google Scholar {optLabel}</label>
                  <input type="url" value={googleScholar} onChange={e => setGoogleScholar(e.target.value)} className={inputClass} />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-white font-semibold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Menyimpan...</>
                  ) : (
                    <><FaEdit /> Perbarui Alumni</>
                  )}
                </button>
                <Link
                  to="/alumni"
                  className="inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-3 px-6 rounded-xl transition-colors text-center"
                >
                  Batal
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAlumni;
